import { ChevronDown, ChevronRight, Pencil, RotateCcw } from 'lucide-react';
import {
  Fragment,
  type KeyboardEvent,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useForm } from 'react-hook-form';
import { FormattedDate } from 'react-intl';

import {
  ANALOG_BANDWIDTH_OPTIONS,
  DEFAULT_MANUAL_BANDWIDTH_MHZ,
  DIGITAL_BANDWIDTH_OPTIONS,
} from '../../constants/suppressions';
import { useIntl } from '../../hooks';
import { formatDuration } from '../../lib/format-duration';
import { hzToMhz, mhzToHz } from '../../lib/frequency';
import { formatOptionalNumber, parseKvNumber } from '../../lib/number-format';
import { zodResolver } from '../../lib/zod-resolver';
import { getStatusTranslationKey, getStatusVariant } from '../../services/detection-status';
import {
  MULTI_FREQUENCY_REPEATABLE_STATUSES,
  REPEATABLE_STATUSES,
  STOPPABLE_STATUSES,
} from '../../services/suppressions.js';
import { type DetectionGroup, DetectionStatus, SignalType } from '../../types';
import {
  buildDetectionGroupSuppressionSchema,
  type DetectionGroupSuppressionFormValues,
} from '../../validations/manual-suppression';
import { DetectionGroupDetectionsRow } from '../detection-groups/DetectionGroupDetectionsRow.js';
import { renderSignalDirectionIcon } from '../SignalDirectionIcon.js';
import { renderSignalTypeIcon } from '../SignalTypeIcon';
import { Badge, Button, Input, TableCell, TableRow } from '../ui';
import { MultiFrequencyDetailsRow } from './MultiFrequencyDetailsRow.js';
import { renderMotionDirectionIcon } from './SignalTrackSummary.js';
import { SuppressDetectionGroupAction } from './SuppressDetectionGroupAction';

export type DetectionGroupSuppressionSubmit = {
  detectionGroupCreatedAt: string;
  detectionGroupId: string;
  durationSec: number;
  group: DetectionGroup;
  isPriority: boolean;
  bandwidthHz?: number;
};

export type DeviceSettingsStatus = 'ready' | 'loading' | 'unavailable';

type DetectionGroupRowProps = {
  canManageSuppressions: boolean;
  isAdminUser?: boolean;
  changeFrequencyPending: boolean;
  colSpan: number;
  createSuppressionPending: boolean;
  defaultSuppressionDuration: number;
  defaultBandwidthMhz?: number;
  deviceId: string;
  isExpanded: boolean;
  group: DetectionGroup;
  maxSuppressionTime: number;
  // Whether `maxSuppressionTime` reflects a resolved settings fetch, and if not, why. Optional:
  // `'ready'` is the safe default for callers that never wire `onRepeatSuppression`.
  deviceSettingsStatus?: DeviceSettingsStatus;
  onChangeFrequency: (opts: {
    groupId: string;
    frequency: number;
    signalType: string;
    duration: number;
  }) => void;
  onStartSuppression: (params: DetectionGroupSuppressionSubmit) => void | Promise<void>;
  onStopSuppression: (
    groupId: string,
    frequency: number,
    signalType: string,
    createdAt: string,
  ) => void;
  // Optional: only `DeviceDetectionGroupsTable` wires this up; other callers leave it unset.
  onStopMultiFrequencySuppression?: (groupId: string, suppressionId: string) => void;
  // Optional: only `DeviceDetectionGroupsTable` wires this up; other callers leave it unset and
  // the button stays hidden (see `canRepeatSuppression`).
  onRepeatSuppression?: (group: DetectionGroup) => void;
  // Optional: only `DeviceDetectionGroupsTable` wires this up; other callers leave it unset and
  // the button stays hidden (see `canRepeatMultiFrequency`).
  onRepeatMultiFrequencySuppression?: (group: DetectionGroup) => void;
  createBroadbandSuppressionPending?: boolean;
  onToggleGroupRow: (groupId: string) => void;
  stopSuppressionPending: boolean;
  // Disables (never hides) the multi-frequency Stop button — the controller's `ensureDeviceOnline`
  // guard means a click while the device is offline is a guaranteed 400. Optional: only
  // `DeviceDetectionGroupsTable` has a device status to give, and
  // `false` is the safe default for a caller that doesn't (the button stays enabled, matching
  // pre-existing behavior for them).
  isDeviceOffline?: boolean;
  showSuppressionActions?: boolean;
  linkedChildren?: DetectionGroup[];
  indentLevel?: 0 | 1;
  onToggleChildren?: () => void;
  childrenOpen?: boolean;
};

export const DetectionGroupRow = memo(function DetectionGroupRow({
  canManageSuppressions,
  isAdminUser = false,
  changeFrequencyPending,
  colSpan,
  createSuppressionPending,
  defaultSuppressionDuration,
  defaultBandwidthMhz = DEFAULT_MANUAL_BANDWIDTH_MHZ,
  deviceId,
  isExpanded,
  group,
  maxSuppressionTime,
  deviceSettingsStatus = 'ready',
  onChangeFrequency,
  onStartSuppression,
  onStopSuppression,
  onStopMultiFrequencySuppression,
  onRepeatSuppression,
  onRepeatMultiFrequencySuppression,
  createBroadbandSuppressionPending = false,
  onToggleGroupRow,
  stopSuppressionPending,
  isDeviceOffline = false,
  showSuppressionActions = true,
  linkedChildren,
  indentLevel = 0,
  onToggleChildren,
  childrenOpen = false,
}: DetectionGroupRowProps) {
  const intl = useIntl();
  const signalLevelValue = parseKvNumber(group.signalLevel);
  const snrValue = parseKvNumber(group.snr);
  const patternType = typeof group.patternType === 'string' ? group.patternType : '-';
  const offsetHz = parseKvNumber(group.offset);
  const lastSeenAt = new Date(group.lastSeenAt);
  const firstSeenAt = new Date(group.firstSeenAt);
  const windowDuration = lastSeenAt.getTime() - firstSeenAt.getTime();
  const detectionsCount = group.detectionsCount ?? 0;
  const suppressionCount = group.suppressionCount ?? 0;
  const suppressionCountClass = suppressionCount > 0 ? 'font-bold text-current' : 'text-current';
  const [elapsedSeconds, setElapsedSeconds] = useState<number>(0);
  const [isEditingFrequency, setIsEditingFrequency] = useState(false);
  const [editedFrequencyMhz, setEditedFrequencyMhz] = useState<string>('');
  const frequencyInputRef = useRef<HTMLInputElement>(null);
  const suppressionFormSchema = useMemo(
    () => buildDetectionGroupSuppressionSchema(maxSuppressionTime),
    [maxSuppressionTime],
  );
  const suppressionForm = useForm<DetectionGroupSuppressionFormValues>({
    resolver: zodResolver(suppressionFormSchema),
    mode: 'onChange',
    defaultValues: {
      duration: String(defaultSuppressionDuration),
      bandwidth: defaultBandwidthMhz,
      isPriority: false,
    },
  });
  const durationValue = suppressionForm.watch('duration') ?? '';
  const bandwidthValue = suppressionForm.watch('bandwidth');
  const priorityCheckbox = suppressionForm.register('isPriority');
  const bandwidthOptions =
    group.signalType === SignalType.DIGITAL ? DIGITAL_BANDWIDTH_OPTIONS : ANALOG_BANDWIDTH_OPTIONS;
  const bandwidthTouchedRef = useRef(false);
  const durationTouchedRef = useRef(false);
  const previousGroupIdRef = useRef(group.id);

  const isSuppressionInProgress = group.status === DetectionStatus.SUPPRESSION_IN_PROGRESS;
  const suppressionDuration = group.suppressionDuration ?? 0;
  const originalFrequencyMhz = hzToMhz(group.frequency);
  // The KIND check, keyed on the server-side discriminator rather than the snapshot's presence.
  const isMultiFrequencyGroup = group.isMultiFrequencySuppression ?? false;
  const multiFrequencySuppression = group.multiFrequencySuppression ?? null;
  // UI half of the API guard: a per-channel mutation on a synthetic group is not inert — stop
  // emits a device `STOP_SUPPRESSION` and change-frequency starts a forced suppression. The whole
  // suppression is stopped via `canStopMultiFrequency` below, which targets the snapshot's own
  // `broadband_suppressions` id.
  const canMutateSuppression =
    showSuppressionActions && canManageSuppressions && !isMultiFrequencyGroup;
  // A degraded (null) snapshot has no id to stop. `onStopMultiFrequencySuppression` is optional
  // and the call below uses `?.`, so without this presence check a caller that omits the prop
  // would render a live destructive Stop button that silently no-ops.
  const canStopMultiFrequency =
    showSuppressionActions &&
    canManageSuppressions &&
    isMultiFrequencyGroup &&
    multiFrequencySuppression != null &&
    onStopMultiFrequencySuppression != null &&
    STOPPABLE_STATUSES.includes(group.status);
  // Disable rather than hide when the device is offline: the
  // controller's `ensureDeviceOnline` guard means a click here is a guaranteed 400. Kept as a
  // disabled term rather than folded into `canStopMultiFrequency` so the affordance stays visible,
  // matching the launcher's "a slow settings fetch disables the button, never hides it" convention.
  const isStopMultiFrequencyDisabled = stopSuppressionPending || isDeviceOffline;
  const canRepeatMultiFrequency =
    showSuppressionActions &&
    canManageSuppressions &&
    isMultiFrequencyGroup &&
    MULTI_FREQUENCY_REPEATABLE_STATUSES.includes(group.status) &&
    onRepeatMultiFrequencySuppression != null;
  // Deliberately no `deviceSettingsStatus` / `maxSuppressionTime` term: the multi path reads no
  // device settings, and a bound here would pre-empt exactly the 400 this button exists to
  // demonstrate. A null snapshot disables rather than hides the button, so a degraded row keeps
  // the affordance visible.
  const isRepeatMultiFrequencyDisabled =
    createBroadbandSuppressionPending || isDeviceOffline || multiFrequencySuppression == null;
  const repeatMultiFrequencyLabelId = isDeviceOffline
    ? 'detections.multiFrequency.repeatTooltipOffline'
    : multiFrequencySuppression == null
      ? 'detections.multiFrequency.repeatTooltipNoSnapshot'
      : 'detections.multiFrequency.repeat';
  const lastSuppressionDuration = group.lastSuppression?.duration;
  // `duration != null` is load-bearing: `CreateSuppressionChannelDto.duration` is required on the
  // backend, so a row whose last suppression has no duration cannot be re-issued. The upper bound
  // applies only when settings are `'ready'` — otherwise `maxSuppressionTime` is a `0` sentinel
  // that would hide the button rather than disable it.
  const canRepeatSuppression =
    canMutateSuppression &&
    REPEATABLE_STATUSES.includes(group.status) &&
    lastSuppressionDuration != null &&
    lastSuppressionDuration >= 1 &&
    (deviceSettingsStatus !== 'ready' || lastSuppressionDuration <= maxSuppressionTime) &&
    onRepeatSuppression != null;
  const isRepeatDisabled =
    createSuppressionPending || isDeviceOffline || deviceSettingsStatus !== 'ready';
  // `aria-label` overrides `title` as the accessible name, so one id feeds both.
  const repeatSuppressionLabelId = isDeviceOffline
    ? 'detections.table.repeatSuppressionTooltipOffline'
    : deviceSettingsStatus === 'loading'
      ? 'detections.table.repeatSuppressionTooltipLoading'
      : deviceSettingsStatus === 'unavailable'
        ? 'detections.table.repeatSuppressionTooltipUnavailable'
        : 'detections.table.repeatSuppression';
  const hasFrequencyChanged =
    isEditingFrequency &&
    editedFrequencyMhz !== '' &&
    parseFloat(editedFrequencyMhz) !== originalFrequencyMhz;
  const canEditFrequency = canMutateSuppression && isSuppressionInProgress;

  const remainingSeconds =
    suppressionDuration > elapsedSeconds ? suppressionDuration - elapsedSeconds : 0;

  useEffect(() => {
    suppressionForm.register('duration');
    suppressionForm.register('bandwidth');
  }, [suppressionForm]);

  // Seed duration/bandwidth from the resolved defaults, but never clobber an
  // operator's in-flight selection until the row switches to a genuinely new
  // group (a dep-value flip such as useDeviceSettings resolving must not
  // overwrite a pick already made for this group).
  useEffect(() => {
    if (previousGroupIdRef.current !== group.id) {
      bandwidthTouchedRef.current = false;
      durationTouchedRef.current = false;
      suppressionForm.reset({
        duration: String(defaultSuppressionDuration),
        bandwidth: defaultBandwidthMhz,
        isPriority: false,
      });
      previousGroupIdRef.current = group.id;
      return;
    }

    if (!durationTouchedRef.current) {
      suppressionForm.setValue('duration', String(defaultSuppressionDuration), {
        shouldValidate: true,
      });
    }
    if (!bandwidthTouchedRef.current) {
      suppressionForm.setValue('bandwidth', defaultBandwidthMhz, { shouldValidate: true });
    }
  }, [defaultSuppressionDuration, defaultBandwidthMhz, group.id, suppressionForm]);

  useEffect(() => {
    if (!isSuppressionInProgress || !group.suppressionStartedAt) {
      setElapsedSeconds(0);
      return;
    }

    const startedAt = new Date(group.suppressionStartedAt).getTime();

    const updateElapsed = () => {
      const now = Date.now();
      const elapsed = Math.floor((now - startedAt) / 1000);
      setElapsedSeconds(Math.max(0, elapsed));
    };

    updateElapsed();
    const interval = setInterval(updateElapsed, 1000);

    return () => clearInterval(interval);
  }, [isSuppressionInProgress, group.suppressionStartedAt]);

  const handleDurationChange = useCallback(
    (value: string) => {
      durationTouchedRef.current = true;
      suppressionForm.setValue('duration', value, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
    },
    [suppressionForm],
  );

  const handleBandwidthChange = useCallback(
    (value: string) => {
      bandwidthTouchedRef.current = true;
      suppressionForm.setValue('bandwidth', Number(value), {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
    },
    [suppressionForm],
  );

  const handleStartEditFrequency = useCallback(() => {
    setEditedFrequencyMhz(String(originalFrequencyMhz));
    setIsEditingFrequency(true);
    setTimeout(() => {
      frequencyInputRef.current?.focus();
      frequencyInputRef.current?.select();
    }, 0);
  }, [originalFrequencyMhz]);

  const handleCancelEditFrequency = useCallback(() => {
    setIsEditingFrequency(false);
  }, []);

  const handleConfirmChangeFrequency = useCallback(() => {
    const newFrequencyHz = mhzToHz(parseFloat(editedFrequencyMhz));
    onChangeFrequency({
      groupId: group.id,
      frequency: newFrequencyHz,
      signalType: group.signalType,
      duration: suppressionDuration,
    });
    setIsEditingFrequency(false);
  }, [editedFrequencyMhz, group.id, group.signalType, onChangeFrequency, suppressionDuration]);

  const handleFrequencyKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && hasFrequencyChanged) {
        e.preventDefault();
        handleConfirmChangeFrequency();
      } else if (e.key === 'Escape') {
        handleCancelEditFrequency();
      }
    },
    [hasFrequencyChanged, handleConfirmChangeFrequency, handleCancelEditFrequency],
  );

  const handleFrequencyBlur = useCallback(() => {
    if (!hasFrequencyChanged) {
      handleCancelEditFrequency();
    }
  }, [hasFrequencyChanged, handleCancelEditFrequency]);

  const handleToggle = useCallback(() => {
    onToggleGroupRow(group.id);
  }, [onToggleGroupRow, group.id]);

  const handleStopSuppression = useCallback(() => {
    onStopSuppression(group.id, group.frequency, group.signalType, group.createdAt);
  }, [onStopSuppression, group.id, group.frequency, group.signalType, group.createdAt]);

  const handleStopMultiFrequencySuppression = useCallback(() => {
    if (!multiFrequencySuppression) return;
    onStopMultiFrequencySuppression?.(group.id, multiFrequencySuppression.id);
  }, [onStopMultiFrequencySuppression, group.id, multiFrequencySuppression]);

  const handleRepeatSuppression = useCallback(() => {
    onRepeatSuppression?.(group);
  }, [onRepeatSuppression, group]);

  const handleRepeatMultiFrequencySuppression = useCallback(() => {
    onRepeatMultiFrequencySuppression?.(group);
  }, [onRepeatMultiFrequencySuppression, group]);

  const handleSubmitSuppression = suppressionForm.handleSubmit((values) => {
    onStartSuppression({
      group,
      durationSec: Number.parseInt(values.duration, 10),
      isPriority: values.isPriority,
      bandwidthHz: mhzToHz(Number(values.bandwidth)),
      detectionGroupId: group.id,
      detectionGroupCreatedAt: group.createdAt,
    });
  });

  // Reset editing state when group status changes
  useEffect(() => {
    if (!isSuppressionInProgress) {
      setIsEditingFrequency(false);
    }
  }, [isSuppressionInProgress]);

  return (
    <Fragment>
      <TableRow data-device-group-id={group.id}>
        <TableCell className={indentLevel === 1 ? 'pl-8' : undefined}>
          <div className="flex items-start justify-center gap-2">
            {linkedChildren && linkedChildren.length > 0 && onToggleChildren && (
              <button
                type="button"
                onClick={onToggleChildren}
                aria-expanded={!!childrenOpen}
                className="p-0.5 hover:bg-background-light rounded transition-colors"
              >
                <span className="sr-only">
                  {intl.formatMessage({ id: 'detections.table.expandChildren' })}
                </span>
                {childrenOpen ? (
                  <ChevronDown className="h-4 w-4" aria-hidden />
                ) : (
                  <ChevronRight className="h-4 w-4" aria-hidden />
                )}
              </button>
            )}
            {renderSignalDirectionIcon(
              group.direction,
              intl.formatMessage({ id: `detections.table.directions.${group.direction}` }),
            )}
            {isAdminUser &&
              group.signalTrack &&
              renderMotionDirectionIcon(
                group.signalTrack.motionDirection,
                group.signalTrack.motionDirectionConfidence,
                intl.formatMessage({
                  id: `detections.table.motionDirections.${group.signalTrack.motionDirection ?? 'unknown'}`,
                }),
                group.signalTrack,
              )}
          </div>
        </TableCell>
        <TableCell>
          {renderSignalTypeIcon(
            group.signalType as SignalType,
            intl.formatMessage({ id: `signalType.${group.signalType}` }),
          )}
        </TableCell>
        <TableCell>
          <Badge
            variant={getStatusVariant(group.status)}
            data-testid="detection-group-status-badge"
          >
            {intl.formatMessage({ id: getStatusTranslationKey(group.status) })}
          </Badge>{' '}
        </TableCell>
        <TableCell className="font-mono" data-testid="detection-group-frequency-cell">
          {canEditFrequency && isEditingFrequency ? (
            <Input
              ref={frequencyInputRef}
              type="number"
              step="0.1"
              value={editedFrequencyMhz}
              onChange={(e) => setEditedFrequencyMhz(e.target.value)}
              onKeyDown={handleFrequencyKeyDown}
              onBlur={handleFrequencyBlur}
              className="w-28 h-7 text-sm font-mono"
              aria-label={intl.formatMessage({ id: 'detections.table.frequency' })}
            />
          ) : (
            <span className="flex items-center gap-1">
              {formatOptionalNumber(hzToMhz(group.frequency))}
              {/* The row keeps showing the representative (lowest) frequency; the badge is what
                  tells the operator the group stands for a whole list. */}
              {isMultiFrequencyGroup && (
                <Badge
                  variant="secondary"
                  className="whitespace-nowrap text-[10px]"
                  data-testid="multi-frequency-group-badge"
                  // A degraded snapshot cannot say across how many frequencies; "0" would be a
                  // specific false number on a row that already admitted it cannot read itself.
                  title={
                    multiFrequencySuppression
                      ? intl.formatMessage(
                          { id: 'detections.multiFrequency.badgeTooltip' },
                          { count: multiFrequencySuppression.channelFrequencyList.length },
                        )
                      : intl.formatMessage({ id: 'detections.multiFrequency.badgeTooltipUnknown' })
                  }
                >
                  {intl.formatMessage({ id: 'detections.multiFrequency.badge' })}
                </Badge>
              )}
              {canEditFrequency && (
                <button
                  type="button"
                  onClick={handleStartEditFrequency}
                  className="p-1 hover:bg-background-light rounded transition-colors"
                  title={intl.formatMessage({ id: 'detections.table.editFrequency' })}
                >
                  <Pencil className="h-3 w-3 text-foreground/60 hover:text-foreground" />
                </button>
              )}
            </span>
          )}
        </TableCell>
        <TableCell>{offsetHz && formatOptionalNumber(hzToMhz(offsetHz))}</TableCell>
        <TableCell>{signalLevelValue && formatOptionalNumber(signalLevelValue)}</TableCell>
        <TableCell>{formatOptionalNumber(snrValue)}</TableCell>
        <TableCell>{patternType}</TableCell>
        <TableCell className="text-xs text-foreground">
          <FormattedDate
            value={lastSeenAt}
            year="numeric"
            month="numeric"
            day="numeric"
            hour="2-digit"
            minute="2-digit"
            second="2-digit"
          />
        </TableCell>
        <TableCell className="relative text-xs">
          {windowDuration > 0 && (
            <div>
              {intl.formatMessage({ id: 'detections.table.window' })}:{' '}
              <FormattedDate value={firstSeenAt} hour="2-digit" minute="2-digit" second="2-digit" />
              {' - '}
              <FormattedDate
                value={lastSeenAt}
                hour="2-digit"
                minute="2-digit"
                second="2-digit"
              />{' '}
              ({formatDuration(windowDuration)})
            </div>
          )}
        </TableCell>
        <TableCell className="border-b-0">
          <div className="flex flex-wrap gap-2 items-center">
            <Button
              variant={isExpanded ? 'default' : 'outline'}
              size="sm"
              onClick={handleToggle}
              aria-pressed={isExpanded}
            >
              {intl.formatMessage({ id: 'detectionGroups.eventsTab' })}&nbsp;
              <span className="tabular-nums">
                ({detectionsCount}&nbsp;/&nbsp;
                <span className={suppressionCountClass}>{suppressionCount}</span>)
              </span>
            </Button>
            {canRepeatMultiFrequency && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleRepeatMultiFrequencySuppression}
                disabled={isRepeatMultiFrequencyDisabled}
                className="text-xs"
                title={intl.formatMessage({ id: repeatMultiFrequencyLabelId })}
                aria-label={intl.formatMessage({ id: repeatMultiFrequencyLabelId })}
                data-testid="multi-frequency-repeat-button"
              >
                <RotateCcw className="h-4 w-4" aria-hidden />
              </Button>
            )}
            {canRepeatSuppression && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleRepeatSuppression}
                disabled={isRepeatDisabled}
                className="text-xs"
                title={intl.formatMessage({ id: repeatSuppressionLabelId })}
                aria-label={intl.formatMessage({ id: repeatSuppressionLabelId })}
                data-testid="repeat-suppression-button"
              >
                <RotateCcw className="h-4 w-4" aria-hidden />
              </Button>
            )}
            {canMutateSuppression && group.status === DetectionStatus.DETECTED && (
              <form className="flex items-center gap-2" onSubmit={handleSubmitSuppression}>
                <input type="hidden" name="detectionGroupId" value={group.id} />
                <input type="hidden" name="detectionGroupCreatedAt" value={group.createdAt} />
                <label className="flex items-center gap-1 text-xs cursor-pointer select-none">
                  <input type="checkbox" className="cursor-pointer" {...priorityCheckbox} />
                  {intl.formatMessage({ id: 'detections.table.priority' })}
                </label>
                <SuppressDetectionGroupAction
                  durationValue={durationValue}
                  durationLabel={intl.formatMessage({
                    id: 'detections.table.suppressionDuration',
                  })}
                  buttonLabel={intl.formatMessage({
                    id: 'detections.table.startSuppression',
                  })}
                  disabled={createSuppressionPending}
                  maxDuration={maxSuppressionTime}
                  onDurationChange={handleDurationChange}
                  bandwidthValueMhz={String(bandwidthValue)}
                  onBandwidthChange={handleBandwidthChange}
                  bandwidthOptions={bandwidthOptions}
                  bandwidthLabel={intl.formatMessage({
                    id: 'detections.table.suppressionBandwidth',
                  })}
                />
              </form>
            )}
            {isSuppressionInProgress && suppressionDuration > 0 && (
              <div className="flex items-center gap-2">
                <Input
                  type="text"
                  name="elapsedDuration"
                  value={remainingSeconds}
                  disabled={true}
                  className="w-20 h-9 rounded-md border border-border px-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/30"
                  suffix={intl.formatMessage({ id: 'detections.table.secondsSuffix' })}
                  aria-label={intl.formatMessage({
                    id: 'detections.table.suppressionDuration',
                  })}
                />
              </div>
            )}
            {canMutateSuppression && STOPPABLE_STATUSES.includes(group.status) && (
              <>
                {hasFrequencyChanged ? (
                  <Button
                    variant="default"
                    size="sm"
                    onClick={handleConfirmChangeFrequency}
                    disabled={changeFrequencyPending}
                    className="text-xs"
                  >
                    {intl.formatMessage({ id: 'detections.table.changeFrequency' })}
                  </Button>
                ) : (
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={handleStopSuppression}
                    disabled={stopSuppressionPending}
                    className="text-xs"
                  >
                    {intl.formatMessage({ id: 'detections.table.stopSuppression' })}
                  </Button>
                )}
              </>
            )}
            {canStopMultiFrequency && (
              <Button
                variant="destructive"
                size="sm"
                onClick={handleStopMultiFrequencySuppression}
                disabled={isStopMultiFrequencyDisabled}
                className="text-xs"
                title={intl.formatMessage({
                  id: isDeviceOffline
                    ? 'detections.multiFrequency.stopTooltipOffline'
                    : 'detections.multiFrequency.stopTooltip',
                })}
                data-testid="multi-frequency-stop-button"
              >
                {intl.formatMessage({ id: 'detections.table.stopSuppression' })}
              </Button>
            )}
          </div>
        </TableCell>
      </TableRow>

      {isExpanded &&
        (isMultiFrequencyGroup ? (
          <MultiFrequencyDetailsRow suppression={multiFrequencySuppression} colSpan={colSpan} />
        ) : (
          <DetectionGroupDetectionsRow
            deviceId={deviceId}
            detectionGroupId={group.id}
            groupCreatedAt={group.createdAt}
            detectionCount={group.detectionsCount}
            suppressionCount={group.suppressionCount}
            colSpan={colSpan}
          />
        ))}
    </Fragment>
  );
});
